import { Accordion, AccordionItem, Card, CardBody, Input } from '@nextui-org/react';
import { checkType, CtyButton, CtyShowToast, CtySpinner, formatKeyForDisplay, NavSidebar, GenericConfigurationFieldRenderer, SimpleDeleteConfirmationModal, AddConfigurationFieldForm, AddFieldDraft, AddFieldType } from '@circuitry-ai/doc-ui';
import { useCreateAiWorkerConfigurationMutation, useUpdateAiWorkerConfigurationMutation, useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import { Icon } from '@iconify/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deepClone, toObject } from './agentutils';
import {
    DEFAULT_ADD_FIELD_DRAFT,
    parseWorkflowConfigurationSections,
    resolveAddFieldValue,
    resolveConfigId,
    buildNestedConfigSection,
    type JsonValue,
} from './agentConfigurationShared';
import { useAgentConfigurationQuery } from './useAgentConfigurationQuery';

export interface GatesAndWeightsProps {
    agent?: any;
    onFlatUpdatesChange?: (updates: Record<string, JsonValue>) => void;
    configId?: string;
    configVersion?: number | string;
    configVersionId?: string;
    hasConfigurationVersions?: boolean;
    activeVersionConfigurationId?: string;
    onVersionUpdated?: () => void;
}

type JsonPrimitive = string | number | boolean | null;

const EMPTY_GATES_CONFIG: Record<string, JsonValue> = {};

const accordionItemClassNames = {
    base: 'bg-content1 border border-divider rounded-lg shadow-none overflow-hidden',
    heading: 'w-full',
    trigger: 'w-full py-3 min-h-[52px]',
    titleWrapper: 'pl-0 ml-0',
    title: 'text-base font-semibold text-default-800 m-0',
    indicator: 'text-default-400',
};

const isObjectValue = (value: JsonValue): value is Record<string, JsonValue> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isPrimitive = (value: JsonValue): value is JsonPrimitive =>
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null;

const getByPath = (source: JsonValue, path: string[]): JsonValue => {
    let current: any = source;
    for (const segment of path) {
        current = current?.[segment];
    }
    return current as JsonValue;
};

const setByPath = (source: JsonValue, path: string[], nextValue: JsonValue): JsonValue => {
    if (path.length === 0) return nextValue;
    const [head, ...tail] = path;
    if (!isObjectValue(source)) return source;
    return {
        ...source,
        [head]: setByPath((source as Record<string, JsonValue>)[head], tail, nextValue),
    };
};

const deleteByPath = (source: JsonValue, path: string[]): JsonValue => {
    if (!isObjectValue(source) || path.length === 0) return source;
    const [head, ...tail] = path;
    if (tail.length === 0) {
        const next = { ...source };
        delete next[head];
        return next;
    }
    return {
        ...source,
        [head]: deleteByPath((source as Record<string, JsonValue>)[head], tail),
    };
};

const areJsonValuesEqual = (left: unknown, right: unknown): boolean => {
    return JSON.stringify(left) === JSON.stringify(right);
};

const stringifyArrayEntry = (entry: JsonValue): string => {
    if (entry == null) return '';
    if (isPrimitive(entry)) return String(entry);
    return JSON.stringify(entry);
};

const GLOBAL_KEY = '__global__';

const navItemIcons: Record<string, string> = {
    [GLOBAL_KEY]: 'material-symbols:domain-rounded',
    requests: 'mdi:file-document-outline',
    VHR: 'mdi:car-info',
    image_analysis: 'mdi:image-search-outline',
    video_analysis: 'mdi:video-outline',
    TW_check: 'mdi:tire',
    dealer_score: 'mdi:store-check-outline',
    valid_policy: 'mdi:shield-check-outline',
    early_late_claim_check: 'mdi:clock-check-outline',
};

export function GatesAndWeights({
    agent,
    onFlatUpdatesChange,
    configId,
    configVersion,
    configVersionId,
    hasConfigurationVersions = false,
    activeVersionConfigurationId = '',
    onVersionUpdated,
}: Readonly<GatesAndWeightsProps>) {
    const { t } = useTranslation();
    const aiworkerId = agent?.aiworker_id ?? '';
    const agentName = agent?.name ?? agent?.aiworker_name ?? 'Agent';
    const { canUpdate } = useEntityResourcePermissions('agent', aiworkerId || undefined);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [configState, setConfigState] = useState<Record<string, JsonValue>>(EMPTY_GATES_CONFIG);
    const [initialConfigState, setInitialConfigState] = useState<Record<string, JsonValue>>(EMPTY_GATES_CONFIG);
    const [expandAll, setExpandAll] = useState(false);
    const [accordionVersion, setAccordionVersion] = useState(0);
    const [flatUpdates, setFlatUpdates] = useState<Record<string, JsonValue>>({});
    const [previewModeByPath, setPreviewModeByPath] = useState<Record<string, boolean>>({});
    const [addFieldDraftByPath, setAddFieldDraftByPath] = useState<Record<string, AddFieldDraft>>({});
    const [addFieldLoadingByPath, setAddFieldLoadingByPath] = useState<Record<string, boolean>>({});
    const [currentCategory, setCurrentCategory] = useState<string>('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [pendingDeletePath, setPendingDeletePath] = useState<string[] | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [updateConfiguration] = useUpdateAiWorkerConfigurationMutation();
    const [createConfiguration] = useCreateAiWorkerConfigurationMutation();

    const {
        selectedConfiguration,
        selectedConfigurationSignature,
        isLoading,
        isFetching,
        isError,
        isConfigurationEditable,
        refetch,
    } = useAgentConfigurationQuery({
        aiworkerId,
        configId,
        configVersion,
        configVersionId,
        canUpdate,
    });

    const parsedWorkflowConfiguration = useMemo(
        () => parseWorkflowConfigurationSections(selectedConfiguration),
        [selectedConfigurationSignature]
    );
    const {
        rootConfigurationId,
        workflowConfig,
        workflowPathPrefix,
        workflowDeletePathPrefix,
        workflowConfigurationId,
    } = parsedWorkflowConfiguration;
    const requiresCreateConfiguration = !hasConfigurationVersions;
    const patchConfigurationId = workflowConfigurationId || rootConfigurationId || activeVersionConfigurationId;

    useEffect(() => {
        const { workflowConfig: nextWorkflowConfig } = parseWorkflowConfigurationSections(selectedConfiguration);
        const nextConfig = deepClone(nextWorkflowConfig);
        setConfigState(nextConfig);
        setInitialConfigState(nextConfig);
        setFlatUpdates({});
        setAddFieldDraftByPath({});
        setAddFieldLoadingByPath({});
        onFlatUpdatesChange?.({});
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only when loaded configuration changes
    }, [selectedConfigurationSignature]);

    const firstLevelActions = useMemo(
        () => ((configState.actions as Record<string, JsonValue>) ?? {}),
        [configState]
    );
    const firstLevelKeys = useMemo(() => Object.keys(firstLevelActions), [firstLevelActions]);

    const globalFields = useMemo(
        () => Object.fromEntries(Object.entries(configState).filter(([key]) => key !== 'actions')) as Record<string, JsonValue>,
        [configState]
    );
    const hasGlobalFields = Object.keys(globalFields).length > 0;

    useEffect(() => {
        const validKeys = [...(hasGlobalFields ? [GLOBAL_KEY] : []), ...firstLevelKeys];
        if (validKeys.length === 0) {
            setCurrentCategory('');
            return;
        }
        if (!validKeys.includes(currentCategory)) {
            setCurrentCategory(validKeys[0]);
        }
    }, [currentCategory, firstLevelKeys, hasGlobalFields]);

    useEffect(() => {
        const media = globalThis.matchMedia('(max-width: 767px)');
        const handler = () => setIsCollapsed(media.matches);
        handler();
        media.addEventListener('change', handler);
        return () => media.removeEventListener('change', handler);
    }, []);

    const navGroups = useMemo(
        () => [
            {
                heading: null,
                items: [
                    ...(hasGlobalFields ? [{
                        key: GLOBAL_KEY,
                        path: `/agents/scores/${GLOBAL_KEY}`,
                        label: t('Main'),
                        icon: navItemIcons[GLOBAL_KEY],
                    }] : []),
                    ...firstLevelKeys.map((key) => ({
                        key,
                        path: `/agents/scores/${key}`,
                        label: t(formatKeyForDisplay(key)),
                        icon: navItemIcons[key] ?? 'mdi:layers-outline',
                    })),
                ],
            },
        ],
        [firstLevelKeys, hasGlobalFields, t]
    );

    const selectedLabel = navGroups[0]?.items.find((item) => item.key === currentCategory)?.label ?? t('Category');
    const isGlobalSelected = currentCategory === GLOBAL_KEY;
    const selectedNodePath: string[] = isGlobalSelected ? [] : ['actions', currentCategory];
    const selectedNode = isGlobalSelected ? globalFields : getByPath(configState, selectedNodePath);

    const updateField = (path: string[], value: JsonValue) => {
        if (!isConfigurationEditable) return;
        const pathKey = `${workflowPathPrefix}.${path.join('.')}`;
        const initialValue = getByPath(initialConfigState, path);

        setConfigState((prev) => setByPath(prev, path, value) as Record<string, JsonValue>);
        setFlatUpdates((prev) => {
            const nextUpdates = { ...prev };
            if (areJsonValuesEqual(value, initialValue)) {
                delete nextUpdates[pathKey];
            } else {
                nextUpdates[pathKey] = value;
            }
            onFlatUpdatesChange?.(nextUpdates);
            return nextUpdates;
        });
    };

    const handleToggleExpandAll = () => {
        setExpandAll((prev) => !prev);
        setAccordionVersion((prev) => prev + 1);
    };

    const handleResetAll = () => {
        if (!isConfigurationEditable) return;
        setConfigState(deepClone(initialConfigState));
        setFlatUpdates({});
        setPreviewModeByPath({});
        onFlatUpdatesChange?.({});
    };

    const handleSave = async () => {
        if (!isConfigurationEditable || !patchConfigurationId || Object.keys(flatUpdates).length === 0) return;
        try {
            setIsSaving(true);
            await updateConfiguration({
                configurationId: patchConfigurationId,
                payload: {
                    scope: 'tenant',
                    ...(flatUpdates as Record<string, unknown>),
                },
            }).unwrap();
            setInitialConfigState(deepClone(configState));
            setFlatUpdates({});
            onFlatUpdatesChange?.({});
            CtyShowToast(checkType.success, t('Scores saved successfully.'));
            onVersionUpdated?.();
        } catch (error: any) {
            const errorMessage = error?.data?.message ?? error?.reason ?? error?.message ?? t('Failed to save Scores.');
            CtyShowToast(checkType.error, String(errorMessage));
        } finally {
            setIsSaving(false);
        }
    };

    const getUpdatedArrayEntryValue = (entry: JsonValue, next: string): JsonValue => {
        if (typeof entry === 'number') {
            return Number(next || 0);
        }

        if (typeof entry === 'boolean') {
            return next === 'true';
        }

        return next;
    };

    const handleArrayEntryChange = (
        value: JsonValue[],
        index: number,
        entry: JsonValue,
        next: string,
        path: string[]
    ) => {
        const nextArray = [...value];
        nextArray[index] = getUpdatedArrayEntryValue(entry, next);
        updateField(path, nextArray);
    };

    const handleArrayEntryRemove = (value: JsonValue[], index: number, path: string[]) => {
        const nextArray = value.filter((_, itemIndex) => itemIndex !== index);
        updateField(path, nextArray);
    };

    const handleFieldDelete = (path: string[]) => {
        if (!path.length || !isConfigurationEditable) return;
        setPendingDeletePath(path);
        setIsDeleteModalOpen(true);
    };

    const getAddFieldPathKey = (path: string[]) => (path.length ? path.join('.') : '__root__');

    const openAddFieldDraft = (path: string[]) => {
        if (!isConfigurationEditable) return;
        const pathKey = getAddFieldPathKey(path);
        setAddFieldDraftByPath((prev) => ({
            ...prev,
            [pathKey]: DEFAULT_ADD_FIELD_DRAFT(),
        }));
    };

    const clearAddFieldDraft = (path: string[]) => {
        const pathKey = getAddFieldPathKey(path);
        setAddFieldDraftByPath((prev) => {
            const next = { ...prev };
            delete next[pathKey];
            return next;
        });
    };

    const updateAddFieldDraft = (path: string[], updater: (draft: AddFieldDraft) => AddFieldDraft) => {
        const pathKey = getAddFieldPathKey(path);
        setAddFieldDraftByPath((prev) => {
            const existing = prev[pathKey];
            if (!existing) return prev;
            return { ...prev, [pathKey]: updater(existing) };
        });
    };

    const addFieldAtPath = async (path: string[]) => {
        if (!isConfigurationEditable) return;
        const pathKey = getAddFieldPathKey(path);
        const draft = addFieldDraftByPath[pathKey];
        if (!draft) return;

        const trimmedKey = draft.keyName.trim();
        if (!trimmedKey) {
            CtyShowToast(checkType.error, t('Key is required.'));
            return;
        }

        const targetNode = (path.length ? getByPath(configState, path) : configState) as JsonValue;
        if (!isObjectValue(targetNode)) {
            CtyShowToast(checkType.error, t('Unable to add field to this section.'));
            return;
        }
        if (Object.prototype.hasOwnProperty.call(targetNode, trimmedKey)) {
            CtyShowToast(checkType.error, t('A field with this key already exists.'));
            return;
        }

        const nextValue = resolveAddFieldValue(draft, t);
        if (nextValue === null) return;

        let createConfigId: string | undefined;
        if (requiresCreateConfiguration) {
            const configIdValue = resolveConfigId(draft, t);
            if (!configIdValue) return;
            createConfigId = configIdValue;
        } else if (!patchConfigurationId) {
            CtyShowToast(checkType.error, t('Unable to add configuration. Active version configuration ID is missing.'));
            return;
        }

        const updatePath = [...path, trimmedKey];
        const updatePathKey = `${workflowPathPrefix}.${updatePath.join('.')}`;

        setAddFieldLoadingByPath((prev) => ({ ...prev, [pathKey]: true }));
        try {
            if (requiresCreateConfiguration) {
                await createConfiguration({
                    aiworkerId,
                    agentName,
                    configId: createConfigId!,
                    config: {
                        workflow_data: buildNestedConfigSection(path, trimmedKey, nextValue),
                    },
                }).unwrap();
            } else {
                await updateConfiguration({
                    configurationId: patchConfigurationId,
                    payload: {
                        scope: 'tenant',
                        [updatePathKey]: nextValue,
                    },
                }).unwrap();
            }

            await refetch();
            clearAddFieldDraft(path);
            CtyShowToast(checkType.success, t('Field added successfully.'));
            onVersionUpdated?.();
        } catch (error: any) {
            const errorMessage = error?.data?.message ?? error?.reason ?? error?.message ?? t('Failed to add field.');
            CtyShowToast(checkType.error, String(errorMessage));
        } finally {
            setAddFieldLoadingByPath((prev) => {
                const next = { ...prev };
                delete next[pathKey];
                return next;
            });
        }
    };

    const handleConfirmDelete = async () => {
        if (!isConfigurationEditable || !pendingDeletePath?.length || !patchConfigurationId) return;

        const pathKey = `${workflowPathPrefix}.${pendingDeletePath.join('.')}`;
        const deletedPath = `${workflowDeletePathPrefix}.${pendingDeletePath.join('.')}`;

        try {
            setIsDeleting(true);
            await updateConfiguration({
                configurationId: patchConfigurationId,
                payload: {
                    scope: 'tenant',
                    'deleted.config': [deletedPath],
                },
            }).unwrap();

            setConfigState((prev) => deleteByPath(prev, pendingDeletePath) as Record<string, JsonValue>);
            setInitialConfigState((prev) => deleteByPath(prev, pendingDeletePath) as Record<string, JsonValue>);
            setFlatUpdates((prev) => {
                const nextUpdates = { ...prev };
                delete nextUpdates[pathKey];
                onFlatUpdatesChange?.(nextUpdates);
                return nextUpdates;
            });
            setPreviewModeByPath((prev) => {
                const next = { ...prev };
                delete next[pendingDeletePath.join('.')];
                return next;
            });
            CtyShowToast(checkType.success, t('Field deleted successfully.'));
            onVersionUpdated?.();
            setIsDeleteModalOpen(false);
            setPendingDeletePath(null);
        } catch (error: any) {
            const errorMessage = error?.data?.message ?? error?.reason ?? error?.message ?? t('Failed to delete field.');
            CtyShowToast(checkType.error, String(errorMessage));
        } finally {
            setIsDeleting(false);
        }
    };

    const renderEditorContent = () => {
        if (!aiworkerId) {
            return <div className="text-sm text-default-500">{t('AI Worker ID is missing for this agent.')}</div>;
        }

        if (isLoading || isFetching) {
            return <CtySpinner size="lg" />;
        }

        if (isError) {
            return <div className="text-sm text-danger">{t('Failed to load scores configuration.')}</div>;
        }

        if (!hasGlobalFields && firstLevelKeys.length === 0) {
            const rootPathKey = getAddFieldPathKey([]);
            const rootDraft = addFieldDraftByPath[rootPathKey];
            const isRootAdding = Boolean(addFieldLoadingByPath[rootPathKey]);

            if (!rootDraft) {
                return (
                    <div className="flex flex-col items-center gap-3 py-6">
                        <p className="text-sm text-default-500">{t('No workflow_data configuration found for this agent.')}</p>
                        {isConfigurationEditable ? (
                            <CtyButton
                                size="sm"
                                color="primary"
                                variant="solid"
                                onPress={() => openAddFieldDraft([])}
                                isDisabled={isRootAdding}
                            >
                                {t('Add Configuration')}
                            </CtyButton>
                        ) : null}
                    </div>
                );
            }

            return (
                <AddConfigurationFieldForm
                    draft={rootDraft}
                    isDisabled={!isConfigurationEditable}
                    isAdding={isRootAdding}
                    showConfigId={requiresCreateConfiguration}
                    onDraftChange={(patch) => updateAddFieldDraft([], (draft) => ({ ...draft, ...patch }))}
                    onCancel={() => clearAddFieldDraft([])}
                    onAdd={() => addFieldAtPath([])}
                    t={t}
                />
            );
        }

        if (isObjectValue(selectedNode)) {
            return renderObjectEditor(selectedNode, selectedNodePath);
        }

        return (
            <div className="text-sm text-default-500">
                {t('Selected node is not an object.')}
            </div>
        );
    };

    const renderPrimitiveEditor = (fieldKey: string, value: JsonPrimitive, path: string[]) => {
        const pathKey = path.join('.');
        const initialValue = getByPath(initialConfigState, path) as JsonValue;

        return (
            <GenericConfigurationFieldRenderer
                fieldKey={fieldKey}
                fieldLabel={t(formatKeyForDisplay(fieldKey))}
                value={value}
                initialValue={initialValue}
                isPreview={Boolean(previewModeByPath[pathKey])}
                onValueChange={(nextValue) => updateField(path, nextValue as JsonValue)}
                onPreviewChange={(nextPreview) =>
                    setPreviewModeByPath((prev) => ({ ...prev, [pathKey]: nextPreview }))
                }
                onResetToDefault={() => updateField(path, initialValue)}
                onDelete={() => handleFieldDelete(path)}
                isDisabled={!isConfigurationEditable}
                t={t}
            />
        );
    };

    const renderArrayEditor = (fieldKey: string, value: JsonValue[], path: string[]) => (
        <Card className="border border-divider shadow-none">
            <CardBody className="gap-2 p-3">
                <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-default-800">{t(formatKeyForDisplay(fieldKey))}</p>
                    <CtyButton size="sm" variant="light" onPress={() => updateField(path, [...value, ''])} isDisabled={!isConfigurationEditable}>
                        {t('Add')}
                    </CtyButton>
                </div>
                {value.map((entry, index) => (
                    <div key={`${fieldKey}-${index}`} className="flex items-center gap-2">
                        <Input
                            value={stringifyArrayEntry(entry)}
                            onValueChange={(next) => handleArrayEntryChange(value, index, entry, next, path)}
                            variant="bordered"
                            size="sm"
                            isDisabled={!isConfigurationEditable}
                        />
                        <CtyButton
                            isIconOnly
                            size="sm"
                            variant="light"
                            color="danger"
                            isDisabled={!isConfigurationEditable}
                            onPress={() => handleArrayEntryRemove(value, index, path)}
                        >
                            <Icon icon="mdi:close" width={16} />
                        </CtyButton>
                    </div>
                ))}
            </CardBody>
        </Card>
    );

    const renderObjectEditor = (
        value: Record<string, JsonValue>,
        path: string[],
        options?: { showAddField?: boolean }
    ): JSX.Element => {
        const showAddField = options?.showAddField ?? true;
        const addFieldPathKey = getAddFieldPathKey(path);
        const addFieldDraft = addFieldDraftByPath[addFieldPathKey];
        const isAddFieldLoading = Boolean(addFieldLoadingByPath[addFieldPathKey]);
        const entries = Object.entries(value);
        const primitiveEntries = entries.filter(([, val]) => isPrimitive(val));
        const nestedEntries = entries.filter(([, val]) => !isPrimitive(val));
        const passthroughEntries = nestedEntries.filter(([key, val]) => key === 'actions' && isObjectValue(val));
        const accordionEntries = nestedEntries.filter(([key, val]) => !(key === 'actions' && isObjectValue(val)));

        return (
            <div className="flex flex-col gap-3">
                {showAddField && (
                    <div className="flex flex-col gap-2">
                        {!addFieldDraft ? (
                            <div className="flex justify-end">
                                <CtyButton
                                    size="sm"
                                    color="primary"
                                    variant="solid"
                                    onPress={() => openAddFieldDraft(path)}
                                    isDisabled={!isConfigurationEditable}
                                >
                                    {t('Add Field')}
                                </CtyButton>
                            </div>
                        ) : (
                            <AddConfigurationFieldForm
                                draft={addFieldDraft}
                                isDisabled={!isConfigurationEditable}
                                isAdding={isAddFieldLoading}
                                onDraftChange={(patch) => updateAddFieldDraft(path, (draft) => ({ ...draft, ...patch }))}
                                onCancel={() => clearAddFieldDraft(path)}
                                onAdd={() => addFieldAtPath(path)}
                                t={t}
                            />
                        )}
                    </div>
                )}

                {primitiveEntries.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {primitiveEntries.map(([key, val]) => (
                            <div
                                key={`${path.join('.')}.${key}`}
                                className={typeof val === 'string' && key.toLowerCase().includes('prompt') ? 'md:col-span-2' : ''}
                            >
                                {renderPrimitiveEditor(key, val as JsonPrimitive, [...path, key])}
                            </div>
                        ))}
                    </div>
                )}

                {accordionEntries.length > 0 && (
                    <Accordion
                        key={`${path.join('.')}-${accordionVersion}`}
                        variant="splitted"
                        selectionMode="multiple"
                        className="!px-0 !gap-2"
                        defaultExpandedKeys={
                            expandAll
                                ? accordionEntries.map(([key]) => [...path, key].join('.'))
                                : []
                        }
                    >
                        {accordionEntries.map(([key, val]) => {
                            const nodePath = [...path, key];
                            if (Array.isArray(val)) {
                                return (
                                    <AccordionItem
                                        key={nodePath.join('.')}
                                        title={t(formatKeyForDisplay(key))}
                                        classNames={accordionItemClassNames}
                                    >
                                        {renderArrayEditor(key, val, nodePath)}
                                    </AccordionItem>
                                );
                            }
                            return (
                                <AccordionItem
                                    key={nodePath.join('.')}
                                    title={t(formatKeyForDisplay(key))}
                                    classNames={accordionItemClassNames}
                                >
                                    {renderObjectEditor(val as Record<string, JsonValue>, nodePath)}
                                </AccordionItem>
                            );
                        })}
                    </Accordion>
                )}

                {passthroughEntries.map(([key, val]) => {
                    const nodePath = [...path, key];
                    return (
                        <div key={nodePath.join('.')}>
                            {renderObjectEditor(val as Record<string, JsonValue>, nodePath, { showAddField: false })}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="flex h-full min-h-0 border border-divider overflow-hidden bg-content1">
            <NavSidebar
                navGroups={navGroups}
                currentSection={currentCategory}
                isCollapsed={isCollapsed}
                navClassName="pt-0"
                onToggleCollapse={() => setIsCollapsed((prev) => !prev)}
                headerTitle={t('Categories')}
                expandMenuLabel={t('Expand menu')}
                collapseMenuLabel={t('Collapse menu')}
                onInternalNavigate={(_, item) => setCurrentCategory(item.key)}
            />

            <main className="flex-1 min-w-0 overflow-y-auto p-2 bg-default-50">
                <Card className="border border-divider shadow-none">
                    <CardBody className="p-0">
                        <div className="flex items-center justify-between border-b border-divider px-3 py-3">
                            <div>
                                <div className="text-lg font-semibold text-default-900">
                                    {selectedLabel}
                                </div>
                                <div className="text-sm text-default-500">{t('Update configuration values for this section.')}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <CtyButton size="sm" variant="bordered" onPress={handleResetAll} isDisabled={!isConfigurationEditable}>
                                    {t('Reset All')}
                                </CtyButton>
                                <CtyButton
                                    size="sm"
                                    color="primary"
                                    variant="solid"
                                    onPress={handleSave}
                                    isDisabled={!isConfigurationEditable || !patchConfigurationId || Object.keys(flatUpdates).length === 0}
                                    isLoading={isSaving}
                                >
                                    {t('Save')}
                                </CtyButton>
                                <CtyButton
                                    size="sm"
                                    color="default"
                                    onPress={handleToggleExpandAll}
                                >
                                    {expandAll ? t('Collapse All') : t('Expand All')}
                                </CtyButton>
                            </div>
                        </div>
                        <div className="p-2">
                            {renderEditorContent()}
                        </div>
                    </CardBody>
                </Card>
            </main>
            <SimpleDeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setIsDeleteModalOpen(false);
                        setPendingDeletePath(null);
                    }
                }}
                body={
                    <div className="flex flex-col gap-2">
                        <p className="text-sm text-default-700">
                            {t('Are you sure you want to delete this configuration field?')}
                        </p>
                        {pendingDeletePath?.length ? (
                            <p className="text-xs text-default-500">{`${workflowDeletePathPrefix}.${pendingDeletePath.join('.')}`}</p>
                        ) : null}
                    </div>
                }
                onConfirm={handleConfirmDelete}
                isConfirmLoading={isDeleting}
            />
        </div>
    );
}

export default GatesAndWeights;
